import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Ban, Globe, Lightbulb, Loader2, MonitorSmartphone, Network, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CS } from "@/constants/testIds/clickshield";

const COUNTRIES = [
  ["RU", "Russia"], ["CN", "China"], ["VN", "Vietnam"], ["IN", "India"], ["ID", "Indonesia"],
  ["NG", "Nigeria"], ["BR", "Brazil"], ["PK", "Pakistan"], ["BD", "Bangladesh"], ["UA", "Ukraine"],
  ["TR", "Turkey"], ["PH", "Philippines"], ["US", "United States"], ["DE", "Germany"], ["GB", "United Kingdom"],
];
const DEVICES = ["Bot/Headless", "Desktop", "Mobile", "Tablet"];
const TYPE_META = {
  country: { icon: Globe, badge: "text-cyan-neon bg-cyan-neon/10 border-cyan-neon/25" },
  ip: { icon: Network, badge: "text-amber-400 bg-amber-500/10 border-amber-500/25" },
  device: { icon: MonitorSmartphone, badge: "text-[#39FF14] bg-[#39FF14]/10 border-[#39FF14]/25" },
};

const inputCls = "px-3 py-2.5 rounded-lg bg-[#040914] border border-white/10 text-white text-sm placeholder:text-slate-600 focus:border-cyan-neon/50 focus:outline-none transition-colors";

export const Rules = () => {
  const [rules, setRules] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [type, setType] = useState("country");
  const [value, setValue] = useState("RU");
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    api.get("/rules").then((r) => setRules(r.data.rules)).catch(() => setRules([]));
    api.get("/rules/suggestions").then((r) => setSuggestions(r.data.suggestions)).catch(() => {});
  }, []);

  const applySuggestion = async (s) => {
    try {
      const { data } = await api.post("/rules", { type: s.type, value: s.value, label: s.label });
      setRules((r) => [data, ...(r || [])]);
      setSuggestions((list) => list.filter((x) => !(x.type === s.type && x.value === s.value)));
      toast.success(`Now blocking ${s.label}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const changeType = (t) => {
    setType(t);
    setValue(t === "country" ? "RU" : t === "device" ? "Bot/Headless" : "");
  };

  const add = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      const label = type === "country" ? (COUNTRIES.find(([c]) => c === value)?.[1] || value) : value;
      const { data } = await api.post("/rules", { type, value, label });
      setRules((r) => [data, ...r]);
      toast.success("Blocking rule added");
      if (type === "ip") setValue("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setAdding(false);
    }
  };

  const remove = async (rule) => {
    try {
      await api.delete(`/rules/${rule.id}`);
      setRules((r) => r.filter((x) => x.id !== rule.id));
      toast.success("Rule removed");
    } catch {
      toast.error("Could not remove rule");
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}
      className="rounded-xl bg-[#0B101A] border border-white/8 p-6">
      <div className="flex items-center gap-2 mb-1">
        <Ban className="w-4 h-4 text-red-400" />
        <h3 className="font-display text-lg text-white">Custom blocking rules</h3>
      </div>
      <p className="text-sm text-slate-400 mb-5">
        Instantly block whole countries, IP ranges or device types. Matching visits are marked <span className="text-red-400">Blocked</span> and redirected away.
      </p>

      {suggestions.length > 0 && (
        <div data-testid="rule-suggestions" className="mb-5 rounded-lg bg-amber-500/5 border border-amber-500/20 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Lightbulb className="w-4 h-4 text-amber-400" />
            <span className="font-mono text-[10px] tracking-widest uppercase text-amber-400">Suggested rules — repeat offenders in your traffic</span>
          </div>
          <div className="space-y-2">
            {suggestions.map((s) => (
              <div
                key={`${s.type}-${s.value}`}
                data-testid={`suggestion-${s.type}-${s.value.replace(/[^a-zA-Z0-9]/g, "-")}`}
                className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg bg-[#040914] border border-white/5 px-4 py-2.5"
              >
                <span className="text-sm text-white">{s.label}</span>
                <span className="text-xs text-slate-500">{s.reason}</span>
                <button
                  data-testid={`suggestion-block-${s.type}-${s.value.replace(/[^a-zA-Z0-9]/g, "-")}`}
                  onClick={() => applySuggestion(s)}
                  className="ml-auto inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-md bg-red-500/90 text-white text-xs font-semibold hover:bg-red-500 transition-colors"
                >
                  <Ban className="w-3 h-3" /> Block
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <form onSubmit={add} className="flex flex-wrap gap-3 mb-5">
        <select
          data-testid={CS.ruleTypeSelect}
          value={type}
          onChange={(e) => changeType(e.target.value)}
          className={`${inputCls} w-40`}
        >
          <option value="country">Country</option>
          <option value="ip">IP / CIDR range</option>
          <option value="device">Device type</option>
        </select>
        {type === "country" && (
          <select data-testid={CS.ruleValueSelect} value={value} onChange={(e) => setValue(e.target.value)} className={`${inputCls} w-52`}>
            {COUNTRIES.map(([code, name]) => (
              <option key={code} value={code} label={`${name} (${code})`} />
            ))}
          </select>
        )}
        {type === "device" && (
          <select data-testid={CS.ruleValueSelect} value={value} onChange={(e) => setValue(e.target.value)} className={`${inputCls} w-52`}>
            {DEVICES.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        )}
        {type === "ip" && (
          <input
            data-testid={CS.ruleValueInput}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            required
            placeholder="203.0.113.7 or 203.0.113.0/24"
            className={`${inputCls} w-64`}
          />
        )}
        <button
          data-testid={CS.ruleAddBtn}
          type="submit"
          disabled={adding || !value}
          className="inline-flex items-center gap-2 px-5 rounded-lg bg-red-500/90 text-white font-semibold text-sm hover:bg-red-500 transition-colors disabled:opacity-60"
        >
          {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Block
        </button>
      </form>

      {!rules ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 text-cyan-neon animate-spin" />
        </div>
      ) : rules.length === 0 ? (
        <p data-testid="rules-empty" className="font-mono text-xs text-slate-500">No custom rules yet — the scoring engine is still protecting you automatically.</p>
      ) : (
        <div data-testid="rules-list" className="space-y-2">
          <AnimatePresence initial={false}>
            {rules.map((r) => {
              const meta = TYPE_META[r.type];
              const Icon = meta.icon;
              return (
                <motion.div
                  key={r.id}
                  data-testid={CS.ruleRow(r.id)}
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, height: 0 }}
                  className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg bg-[#040914] border border-white/5 px-4 py-3"
                >
                  <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded border font-mono text-[9px] tracking-wider uppercase ${meta.badge}`}>
                    <Icon className="w-3 h-3" /> {r.type}
                  </span>
                  <span className="text-sm text-white">{r.label}</span>
                  {r.type === "country" && <span className="font-mono text-xs text-slate-500">{r.value}</span>}
                  <span className="ml-auto font-mono text-xs text-slate-500">
                    <span className="text-red-400">{r.hits.toLocaleString()}</span> blocked
                  </span>
                  <button
                    data-testid={CS.ruleDelete(r.id)}
                    onClick={() => remove(r)}
                    className="p-1.5 rounded-md text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}
    </motion.div>
  );
};
